import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaCheck, FaStickyNote } from 'react-icons/fa';
import axios from 'axios';

const VaerkstedDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const eftersynId = searchParams.get('eftersyn_id');
  const eftersynType = searchParams.get('type') || '';
  const inventarNavn = searchParams.get('inventar') || '';
  const afdeling = searchParams.get('afdeling') || '';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [mekanikere, setMekanikere] = useState([]);
  const [mechanicSignature, setMechanicSignature] = useState('');
  const [subcategoryResults, setSubcategoryResults] = useState({});
  const [subcategoryFields, setSubcategoryFields] = useState({});
  const [notesActive, setNotesActive] = useState({});
  const [activeSubcategories, setActiveSubcategories] = useState({});

  const sessionKey = useMemo(
    () => `workshop:${eftersynId}:${eftersynType}`,
    [eftersynId, eftersynType]
  );
  const restoredFromSession = useRef(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Find matchende kategori(er)
      const catsRes = await axios.get('/api/services/categories');
      const allCats = catsRes.data || [];
      const norm = (s) => (s || '').toLowerCase();
      const base = norm(eftersynType);
      const matching = allCats.filter(c => {
        const name = norm(c.name);
        if (base === 'el eftersyn') return name.startsWith('el eftersyn');
        if (base === 'kæde eftersyn' || base === 'kaede eftersyn') return name.includes('kæde') || name.includes('kaede');
        if (base === 'stige eftersyn') return name.includes('stige');
        return name === base;
      });
      if (matching.length === 0) {
        setCategories([]);
        setLoading(false);
        return;
      }
      const categoryIds = matching.map(c => c.id);
      const catsWithSubs = await axios.get(`/api/services/categories/${categoryIds.join(',')}`);
      setCategories(catsWithSubs.data || []);

      // Hent felter for hver underkategori
      const subcategoryIds = (catsWithSubs.data || [])
        .filter(item => item.subcategory_id)
        .map(item => item.subcategory_id);
      const uniqueSubIds = Array.from(new Set(subcategoryIds));
      const fieldsArr = await Promise.all(
        uniqueSubIds.map(id => axios.get(`/api/indstillinger/eftersynsskema/subcategories/${id}/fields`).then(r => ({ id, fields: r.data })))
      );
      const fieldsMap = {};
      fieldsArr.forEach(({ id, fields }) => { 
        // Sorter felter efter sort_order for korrekt rækkefølge
        const sortedFields = (fields || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        fieldsMap[id] = sortedFields; 
      });
      setSubcategoryFields(fieldsMap);

      // Mekanikere til underskrift
      const mekRes = await axios.get('/api/services/mekanikere');
      setMekanikere(mekRes.data || []);

      setLoading(false);
    } catch (e) {
      console.error('Error loading workshop details:', e);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading || restoredFromSession.current) return;
    try {
      const raw = localStorage.getItem(sessionKey);
      if (raw) {
        const payload = JSON.parse(raw);
        if (payload && typeof payload.ts === 'number') {
          const maxAge = 7 * 24 * 60 * 60 * 1000;
          if (Date.now() - payload.ts <= maxAge) {
            if (payload.mechanicSignature) setMechanicSignature(payload.mechanicSignature);
            if (payload.activeSubcategories) setActiveSubcategories(payload.activeSubcategories);
            if (payload.subcategoryResults) setSubcategoryResults(payload.subcategoryResults);
            if (payload.notesActive) setNotesActive(payload.notesActive);
          } else {
            localStorage.removeItem(sessionKey);
          }
        }
      }
    } catch (_) {}
    restoredFromSession.current = true;
  }, [loading, sessionKey]);

  useEffect(() => {
    if (loading) return;
    try {
      const payload = {
        ts: Date.now(),
        mechanicSignature,
        activeSubcategories,
        subcategoryResults,
        notesActive,
      };
      localStorage.setItem(sessionKey, JSON.stringify(payload));
    } catch (_) {}
  }, [mechanicSignature, activeSubcategories, subcategoryResults, notesActive, sessionKey, loading]);

  const handleSubcategoryChange = (subcategoryId, fieldName, value, index = 0) => {
    setSubcategoryResults(prev => {
      const current = prev[subcategoryId] || {};
      const fieldData = current[fieldName] || [];
      while (fieldData.length <= index) fieldData.push('');
      fieldData[index] = value;
      return { ...prev, [subcategoryId]: { ...current, [fieldName]: fieldData } };
    });
  };

  const addSubcategoryRow = (subcategoryId) => {
    setSubcategoryResults(prev => {
      const current = prev[subcategoryId] || {};
      const next = { ...current };
      const fields = subcategoryFields[subcategoryId] || [];
      fields.forEach(field => {
        const key = field.name;
        const arr = Array.isArray(next[key]) ? next[key].slice() : [];
        arr.push('');
        next[key] = arr;
      });
      const ia = Array.isArray(next.is_approved) ? next.is_approved.slice() : [];
      ia.push('OK');
      next.is_approved = ia;
      const notes = Array.isArray(next.notes) ? next.notes.slice() : [];
      notes.push('');
      next.notes = notes;
      const desc = Array.isArray(next.description) ? next.description.slice() : [];
      desc.push('');
      next.description = desc;
      if (!Array.isArray(next.signed_by) || next.signed_by.length === 0) {
        next.signed_by = [mechanicSignature || ''];
      }
      return { ...prev, [subcategoryId]: next };
    });
  };

  const removeSubcategoryRow = (subcategoryId, index) => {
    setSubcategoryResults(prev => {
      const current = prev[subcategoryId] || {};
      const next = { ...current };
      Object.keys(next).forEach(key => {
        if (Array.isArray(next[key])) {
          const arr = next[key].slice();
          if (index >= 0 && index < arr.length) {
            arr.splice(index, 1);
            next[key] = arr;
          }
        }
      });
      return { ...prev, [subcategoryId]: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!eftersynId) { alert('Mangler eftersyn ID'); return; }
    if (!mechanicSignature.trim()) { alert('Vælg venligst en mekaniker'); return; }
    setSaving(true);
    try {
      await axios.post('/api/vaerksted/save', {
        eftersyn_id: eftersynId,
        subcategory_results: subcategoryResults,
        mechanic_signature: mechanicSignature,
      });
      alert('Eftersyn gemt');
      navigate('/vaerksted');
    } catch (error) {
      console.error('Error saving workshop inspection:', error);
      alert('Der opstod en fejl under gem.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  const groupedCategories = {};
  categories.forEach(item => {
    if (!groupedCategories[item.category_id]) {
      groupedCategories[item.category_id] = {
        id: item.category_id,
        name: item.category_name,
        subcategories: []
      };
    }
    if (item.subcategory_id) {
      groupedCategories[item.category_id].subcategories.push({
        id: item.subcategory_id,
        name: item.subcategory_name
      });
    }
  });

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/vaerksted')} className="p-2 text-gray-600 hover:text-gray-800">
          <FaArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{eftersynType} – Skema</h1>
          <p className="text-gray-600">{inventarNavn}{afdeling ? ` • ${afdeling}` : ''}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mekaniker *</label>
              <select
                value={mechanicSignature}
                onChange={(e) => setMechanicSignature(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
              >
                <option value="">Vælg mekaniker</option>
                {mekanikere.map((m, i) => (
                  <option key={i} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {Object.values(groupedCategories).map((category) => (
          <div key={category.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow">
            <div className="bg-gray-50 px-4 sm:px-6 py-3 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{category.name}</h2>
            </div>
            <div className="p-4 sm:p-6 space-y-6">
              {category.subcategories.map((subcategory) => (
                <div key={subcategory.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">{subcategory.name}</h3>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const next = !Boolean(activeSubcategories[subcategory.id]);
                          setActiveSubcategories(prev => ({ ...prev, [subcategory.id]: next }));
                          if (next) {
                            addSubcategoryRow(subcategory.id);
                          } else {
                            setSubcategoryResults(prev => ({ ...prev, [subcategory.id]: {} }));
                          }
                        }}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${
                          activeSubcategories[subcategory.id] ? 'bg-green-600 border-green-700 text-white' : 'bg-white border-gray-300 text-gray-500'
                        }`}
                        title={activeSubcategories[subcategory.id] ? 'Fjern godkendelse' : 'Godkend'}
                      >
                        <FaCheck className="text-sm" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!activeSubcategories[subcategory.id]) {
                            setActiveSubcategories(prev => ({ ...prev, [subcategory.id]: true }));
                          }
                          const next = !Boolean(notesActive[subcategory.id]);
                          setNotesActive(prev => ({ ...prev, [subcategory.id]: next }));
                          if (!next) {
                            setSubcategoryResults(prev => {
                              const current = prev[subcategory.id] || {};
                              return { ...prev, [subcategory.id]: { ...current, notes: [] } };
                            });
                          } else {
                            const rows = (subcategoryResults[subcategory.id]?.is_approved || []).length;
                            if (!rows) addSubcategoryRow(subcategory.id);
                          }
                        }}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${
                          (notesActive[subcategory.id] || ((subcategoryResults[subcategory.id]?.notes || []).some(n => n && String(n).trim() !== '')))
                            ? 'bg-blue-600 border-blue-700 text-white'
                            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                        title="Tilføj noter-linje"
                      >
                        <FaStickyNote className="text-sm" />
                      </button>
                    </div>
                  </div>

                  {Boolean(activeSubcategories[subcategory.id]) && (
                    <>
                      {(subcategoryResults[subcategory.id]?.is_approved || ['']).map((_, index) => {
                        const fieldsForSub = (subcategoryFields[subcategory.id] || []);
                        const hasFields = fieldsForSub.length > 0;
                        const noteVal = subcategoryResults[subcategory.id]?.notes?.[index] || '';
                        const hasFieldContent = hasFields && fieldsForSub.some(field => {
                          const val = subcategoryResults[subcategory.id]?.[field.name]?.[index];
                          return val && String(val).trim() !== '';
                        });
                        const hasContent = hasFieldContent || (noteVal && String(noteVal).trim() !== '');
                        const rowClasses = `grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 ${hasContent ? 'p-4 bg-gray-50 rounded' : ''}`;
                        return (
                          <div key={index} className={rowClasses}>
                            {fieldsForSub.map(field => {
                              const value = subcategoryResults[subcategory.id]?.[field.name]?.[index] || '';
                              const onChange = (val) => handleSubcategoryChange(subcategory.id, field.name, val, index);
                              if (field.type === 'textarea') {
                                return (
                                  <div key={field.name} className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                    <textarea
                                      value={value}
                                      onChange={(e) => onChange(e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                      rows={3}
                                    />
                                  </div>
                                );
                              }
                              if (field.type === 'select') {
                                let options = [];
                                try { options = field.options ? JSON.parse(field.options) : []; } catch (_) { options = []; }
                                return (
                                  <div key={field.name}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                    <select
                                      value={value}
                                      onChange={(e) => onChange(e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                    >
                                      <option value="">-- Vælg --</option>
                                      {options.map((opt, i) => (
                                        <option key={i} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  </div>
                                );
                              }
                              if (field.type === 'date') {
                                return (
                                  <div key={field.name}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                    <input
                                      type="date"
                                      value={value}
                                      onChange={(e) => onChange(e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                    />
                                  </div>
                                );
                              }
                              if (field.type === 'checkbox') {
                                return (
                                  <div key={field.name} className="flex items-center mt-6">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(value)}
                                      onChange={(e) => onChange(e.target.checked ? '1' : '')}
                                      className="mr-2"
                                    />
                                    <label className="text-sm text-gray-700">{field.label}</label>
                                  </div>
                                );
                              }
                              return (
                                <div key={field.name}>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                  <input
                                    type="text"
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                  />
                                </div>
                              );
                            })}

                            {(notesActive[subcategory.id] || ((subcategoryResults[subcategory.id]?.notes?.[index] || '').trim() !== '')) && (
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Noter</label>
                                <textarea
                                  value={(subcategoryResults[subcategory.id]?.notes?.[index]) || ''}
                                  onChange={(e) => handleSubcategoryChange(subcategory.id, 'notes', e.target.value, index)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                  rows={2}
                                  placeholder="Tilføj noter..."
                                />
                              </div>
                            )}
                            {(subcategoryResults[subcategory.id]?.is_approved || ['']).length > 1 && (
                              <div className="md:col-span-2 flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => removeSubcategoryRow(subcategory.id, index)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Fjern række
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {(subcategoryFields[subcategory.id] || []).some(f => Boolean(f.repeatable)) && (
                        <button
                          type="button"
                          onClick={() => addSubcategoryRow(subcategory.id)}
                          className="text-falck-blue hover:text-blue-700 text-sm font-medium"
                        >
                          + Tilføj en linje mere
                        </button>
                      )}

                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Underskrift (mekaniker)</label>
                          <select
                            value={(subcategoryResults[subcategory.id]?.signed_by?.[0]) || (mechanicSignature || '')}
                            onChange={(e) => handleSubcategoryChange(subcategory.id, 'signed_by', e.target.value, 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                          >
                            <option value="">Vælg mekaniker</option>
                            {mekanikere.map((m, i) => (
                              <option key={i} value={m}>{m}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/vaerksted')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Tilbage
          </button>
          <button
            type="submit"
            disabled={saving}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md flex items-center gap-2 transition-colors ${
              saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-falck-blue hover:bg-blue-700'
            }`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Gemmer...
              </>
            ) : (
              <>
                <FaSave />
                Gem eftersyn
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VaerkstedDetails;
